import { useEffect, useState, type ReactNode } from "react";

import {
  getPersonalLawyerProfile,
  createPersonalLawyerRecharge,
  getPersonalLawyerRecharge,
  getPersonalLawyerWallet,
  getPersonalLawyerInvitation,
  getPersonalLawyerRewards,
  type PersonalLawyerProfile as Profile,
} from "../api/client";
import { IdentityVerification } from "./IdentityVerification";
import { PersonalProfile } from "./PersonalProfile";
import { BalanceAccount, type PersonalWallet } from "./BalanceAccount";
import { InviteLawyer, type LawyerInvitation } from "./InviteLawyer";
import { MyRewards, type RewardSummary } from "./MyRewards";

export type PersonalCenterArea = "profile" | "verification" | "rewards" | "balance" | "invite";

export function PersonalLawyerCenterLayout({
  lawyerName,
  lawyerNumber,
  active,
  onSelect,
  onHome,
  children,
}: {
  lawyerName: string;
  lawyerNumber: string;
  active: PersonalCenterArea;
  onSelect: (area: PersonalCenterArea) => void;
  onHome: () => void;
  children: ReactNode;
}) {
  return (
    <div className="personal-lawyer-center">
      <header className="personal-lawyer-topbar">
        <strong>中承信合数据服务平台</strong>
        <button type="button" onClick={onHome}>返回产品服务</button>
      </header>
      <div className="personal-lawyer-workspace">
        <aside>
          <div className="personal-lawyer-persona"><span>{lawyerName.slice(0, 1)}</span><div><strong>{lawyerName}律师</strong><small>{lawyerNumber}</small></div></div>
          <nav aria-label="个人中心">
            <button className={active === "profile" ? "is-active" : ""} type="button" onClick={() => onSelect("profile")}>基本资料</button>
            <button className={active === "verification" ? "is-active" : ""} type="button" onClick={() => onSelect("verification")}>实名认证</button>
            <button className={active === "rewards" ? "is-active" : ""} type="button" onClick={() => onSelect("rewards")}>我的奖励</button>
            <button className={active === "balance" ? "is-active" : ""} type="button" onClick={() => onSelect("balance")}>余额账户</button>
            <button className={active === "invite" ? "is-active" : ""} type="button" onClick={() => onSelect("invite")}>邀请律师</button>
          </nav>
        </aside>
        <main>{children}</main>
      </div>
    </div>
  );
}

export function PersonalLawyerCenter({ onHome }: { onHome: () => void }) {
  const [active, setActive] = useState<PersonalCenterArea>("profile");
  const [profile, setProfile] = useState<Profile | null>(null);
  const [wallet, setWallet] = useState<PersonalWallet | null>(null);
  const [invitation, setInvitation] = useState<LawyerInvitation | null>(null);
  const [rewards, setRewards] = useState<RewardSummary | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    void getPersonalLawyerProfile().then(setProfile).catch((reason: Error) => setError(reason.message));
    void getPersonalLawyerWallet().then(setWallet).catch((reason: Error) => setError(reason.message));
    void getPersonalLawyerInvitation().then(setInvitation).catch((reason: Error) => setError(reason.message));
    void getPersonalLawyerRewards().then(setRewards).catch((reason: Error) => setError(reason.message));
  }, []);
  if (error) return <div className="session-loading">{error}</div>;
  if (!profile) return <div className="session-loading">正在加载个人中心…</div>;
  return (
    <PersonalLawyerCenterLayout
      lawyerName={profile.lawyer_name}
      lawyerNumber={profile.lawyer_number}
      active={active}
      onSelect={setActive}
      onHome={onHome}
    >
      {active === "profile" && <PersonalProfile profile={profile} />}
      {active === "verification" && <IdentityVerification profile={profile} onSubmitted={setProfile} />}
      {active === "balance" && wallet && <BalanceAccount wallet={wallet} onRecharge={async (amount, channel) => {
        return createPersonalLawyerRecharge(amount, channel);
      }} onCheckRecharge={getPersonalLawyerRecharge} onBalanceUpdated={async () => {
        setWallet(await getPersonalLawyerWallet(wallet.recharge_page));
      }} onRechargePageChange={async (page) => {
        setWallet(await getPersonalLawyerWallet(page));
      }} />}
      {active === "rewards" && rewards && <MyRewards summary={rewards} onInvitePageChange={async (page) => {
        setRewards(await getPersonalLawyerRewards(page, rewards.records_page));
      }} onRecordPageChange={async (page) => {
        setRewards(await getPersonalLawyerRewards(rewards.direct_invites_page, page));
      }} />}
      {active === "invite" && invitation && <InviteLawyer invitation={invitation} />}
    </PersonalLawyerCenterLayout>
  );
}
